import type { PageTrafficMeta } from '@xjlv2/traffic-tracker'
import type { PermissionManagementSection, WorkspaceTab } from '../types'

/**
 * camelCase 权限分区名 → 页码段。服务端页码白名单为纯小写字符集
 * `[a-z][a-z0-9-]*(\.[a-z][a-z0-9-]*){1,5}`（TrafficIngestionService /
 * TrafficPageCatalog 一致），camelCase 分区必须显式映射为小写连字符页码，
 * 否则事件 100% 被 INVALID_PAGE_CODE 拒收、服务端目录配置无法注册。
 */
const PERMISSION_SECTION_SLUGS: Readonly<Record<PermissionManagementSection, string>> = Object.freeze({
  permissions: 'permissions',
  menus: 'menus',
  depts: 'depts',
  roles: 'roles',
  users: 'users',
  clientRoles: 'client-roles',
  clientUsers: 'client-users',
  moduleUsers: 'module-users',
  params: 'params',
  dicts: 'dicts',
  openPlatform: 'open-platform'
})

/**
 * 壳内 system 模块的流量页目录（route → 页码），与 App.vue 的 tab /
 * permission section 对应。页码需在服务端 `saas.traffic.page-catalog`
 * 注册后才会上报成功（见 README「流量采集」），且租户须开通 system 模块。
 */
export const SHELL_TRAFFIC_PAGES: Readonly<Record<string, PageTrafficMeta>> = Object.freeze({
  '/system/workbench/dashboard': { moduleCode: 'system', pageCode: 'system.workbench.dashboard' },
  '/system/workbench/notifications': { moduleCode: 'system', pageCode: 'system.workbench.notifications' },
  '/system/workbench/behavior-analytics': { moduleCode: 'system', pageCode: 'system.workbench.behavior-analytics' },
  '/system/permission/permissions': { moduleCode: 'system', pageCode: 'system.permission.permissions' },
  '/system/permission/menus': { moduleCode: 'system', pageCode: 'system.permission.menus' },
  '/system/permission/depts': { moduleCode: 'system', pageCode: 'system.permission.depts' },
  '/system/permission/roles': { moduleCode: 'system', pageCode: 'system.permission.roles' },
  '/system/permission/users': { moduleCode: 'system', pageCode: 'system.permission.users' },
  '/system/permission/client-roles': { moduleCode: 'system', pageCode: 'system.permission.client-roles' },
  '/system/permission/client-users': { moduleCode: 'system', pageCode: 'system.permission.client-users' },
  '/system/permission/module-users': { moduleCode: 'system', pageCode: 'system.permission.module-users' },
  '/system/permission/params': { moduleCode: 'system', pageCode: 'system.permission.params' },
  '/system/permission/dicts': { moduleCode: 'system', pageCode: 'system.permission.dicts' },
  '/system/permission/open-platform': { moduleCode: 'system', pageCode: 'system.permission.open-platform' }
})

/** 打开模块菜单（MFE 视图）时的哨兵路由：不在目录内，tracker 收到后暂停 system 页采集。 */
export const SHELL_MODULE_HOST_ROUTE = '/module/module-host'

export interface ShellTrafficRouteInput {
  /** 当前是否停留在模块菜单（ModuleHost/MFE）视图。 */
  moduleMenuActive: boolean
  tab: WorkspaceTab
  permissionSection: PermissionManagementSection
}

/** 工作台 tab 的 camelCase key → 小写连字符页码段（服务端页码字符集不含大写）。 */
const WORKBENCH_TAB_SLUGS: Partial<Record<WorkspaceTab, string>> = {
  behaviorAnalytics: 'behavior-analytics'
}

/** 把壳内导航状态映射为流量 route；模块视图返回哨兵路由（不在页目录内）。 */
export function shellTrafficRouteFor({ moduleMenuActive, tab, permissionSection }: ShellTrafficRouteInput): string {
  if (moduleMenuActive) return SHELL_MODULE_HOST_ROUTE
  if (tab === 'permissionManagement') return `/system/permission/${PERMISSION_SECTION_SLUGS[permissionSection]}`
  return `/system/workbench/${WORKBENCH_TAB_SLUGS[tab] ?? tab}`
}

type ShellTrafficPageReporter = (rawRoute: string) => void

let shellTrafficReporter: ShellTrafficPageReporter | null = null
let lastReportedShellRoute: string | null = null

/** 装配完成时由 traffic-shell 注入真实 reporter；默认（未装配/off）为空操作。 */
export function setShellTrafficReporter(reporter: ShellTrafficPageReporter | null): void {
  shellTrafficReporter = reporter
}

/**
 * 上报壳内页面切换。流量采集绝不打断宿主：未装配（off）或租户 scope
 * 未就绪（未登录）时静默跳过，同时记录最近路由供装配后补报。
 */
export function reportShellPageView(rawRoute: string): void {
  lastReportedShellRoute = rawRoute
  try {
    shellTrafficReporter?.(rawRoute)
  } catch {
    // tenant scope 尚未就绪（未登录）等情况直接跳过。
  }
}

/** 装配完成后补报最近一次请求的页面（覆盖装配竞态期间的首屏）。 */
export function replayLastShellRoute(): void {
  if (lastReportedShellRoute !== null) reportShellPageView(lastReportedShellRoute)
}

/** 清空 reporter 与待补报路由（测试隔离用；生产路径无需调用）。 */
export function resetShellTraffic(): void {
  shellTrafficReporter = null
  lastReportedShellRoute = null
}
